import { hasMarketingConsent } from "@/lib/meta-pixel";

/**
 * Origen publicitario del registro (Google Ads, Meta Ads, UTM). Solo con
 * consentimiento de marketing: combina la cookie `rm_attr` que deja la landing
 * en .reservamanager.com, los parámetros de la URL actual (más recientes) y las
 * cookies del Pixel de Meta (_fbc/_fbp).
 */

export interface AdClickPayload {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  metaEventId?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  landingPage?: string;
  /** Dominio de procedencia ("google.com"), tal como lo dejó la landing. */
  referrer?: string;
  capturedAt?: string;
  consent: true;
}

const ATTR_COOKIE = "rm_attr";

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  return document.cookie
    .split(";")
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix))
    ?.slice(prefix.length);
}

function readStoredAttribution(): Record<string, string> {
  try {
    const raw = readCookie(ATTR_COOKIE);
    const parsed: unknown = raw ? JSON.parse(decodeURIComponent(raw)) : null;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

/** Identificador de evento compartido entre el Pixel y la Conversions API. */
export function newMetaEventId(): string {
  const random = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}${Math.random()}`;
  return `reg_${random.replace(/[^A-Za-z0-9]/g, "").slice(0, 32)}`;
}

export function getAdClickPayload(metaEventId?: string): AdClickPayload | undefined {
  if (!hasMarketingConsent()) return undefined;
  const stored = readStoredAttribution();
  const params = new URLSearchParams(window.location.search);
  const pick = (key: string): string | undefined => params.get(key)?.slice(0, 256) || stored[key] || undefined;
  const clickInUrl = ["gclid", "gbraid", "wbraid", "fbclid"].some((key) => params.get(key));
  const payload: AdClickPayload = {
    gclid: pick("gclid"),
    gbraid: pick("gbraid"),
    wbraid: pick("wbraid"),
    fbclid: pick("fbclid"),
    fbc: readCookie("_fbc"),
    fbp: readCookie("_fbp"),
    metaEventId,
    utmSource: pick("utm_source"),
    utmMedium: pick("utm_medium"),
    utmCampaign: pick("utm_campaign"),
    utmTerm: pick("utm_term"),
    utmContent: pick("utm_content"),
    landingPage: clickInUrl ? `${window.location.origin}${window.location.pathname}` : stored.lp,
    referrer: stored.referrer,
    capturedAt: clickInUrl ? new Date().toISOString() : stored.capturedAt,
    consent: true,
  };
  const hasSignal =
    payload.gclid ||
    payload.gbraid ||
    payload.wbraid ||
    payload.fbclid ||
    payload.fbc ||
    payload.fbp ||
    payload.utmSource ||
    payload.utmCampaign;
  return hasSignal ? payload : undefined;
}

/** Al retirar el consentimiento de marketing, el origen publicitario se borra. */
export function clearAdClickAttribution(): void {
  try {
    document.cookie = `${ATTR_COOKIE}=; domain=.reservamanager.com; path=/; max-age=0; secure; samesite=lax`;
  } catch {
    // Sin acceso a cookies: nada que borrar.
  }
}
